import PropTypes from 'prop-types';
import { useFormikContext, Formik } from 'formik';
import Button from '@adsk/alloy-react-button';
import Panel from '@adsk/alloy-react-panel';
import { translate } from '../helpers/i18n';
import { useRfiForm } from './RfiFormProvider';
import RfiForm from './RfiForm';
import { changeTimeZone } from '../helpers';
import getAssigneesFromTransition from './helpers/getAssigneeFromTransition';
import { useDueDateTimezone } from '../hooks';
import { Loader } from '../components';
import { EMPTY_OBJECT } from '../constants';
import { RfiPriority } from '../sdk';

const nowInIsoString = new Date().toISOString();

const renderFooter = ({ style }) => {
  const { submitForm } = useFormikContext();
  const { isSubmitting } = useRfiForm();

  const createRfiButtonText = isSubmitting
    ? translate('new.creating')
    : translate('navigation.create');

  return (
    <div style={style}>
      <Button
        size={Button.SIZES.MEDIUM}
        variant={Button.VARIANTS.PRIMARY}
        onClick={() => {
          submitForm();
        }}
        disabled={isSubmitting}
        style={{
          width: '100%',
        }}
      >
        {createRfiButtonText}
      </Button>
    </div>
  );
};

const RfiCreateForm = ({ open, onClose, onCreateSuccess, initialFields = EMPTY_OBJECT }) => {
  const { dueDateTimeZone } = useDueDateTimezone(nowInIsoString);
  const rfiFormData = useRfiForm();

  const handleCreatedRfi = (newRfi) => {
    onCreateSuccess(newRfi);
    onClose();
  };

  const dueDate = initialFields.dueDate && changeTimeZone(initialFields.dueDate, dueDateTimeZone);
  const assignedTo = getAssigneesFromTransition(
    initialFields.assignedTo,
    rfiFormData.selectedTransition,
  );

  return (
    <Formik
      initialValues={{ ...rfiFormData.defaultValues, ...initialFields, assignedTo, dueDate }}
      onSubmit={async (...args) => {
        if (rfiFormData.createRfi) {
          const newRfi = await rfiFormData.createRfi(...args, dueDateTimeZone);
          handleCreatedRfi(newRfi);
        }
      }}
      validationSchema={rfiFormData.validationSchema}
    >
      <Panel
        onClose={onClose}
        title={translate('navigation.create')}
        open={open}
        floating
        {...(rfiFormData.ready ? { renderFooter } : {})}
      >
        <Panel.Body>
          {rfiFormData.ready ? <RfiForm /> : <Loader size={Loader.SIZES.LARGE} />}
        </Panel.Body>
      </Panel>
    </Formik>
  );
};
RfiCreateForm.propTypes = {
  /**
   * Callback that's being called when close the modal.
   */
  onClose: PropTypes.func,
  /**
   * The open state of the panel.
   */
  open: PropTypes.bool,
  /**
   * handler for create success event. Used to create a link after the entity was created.
   */
  onCreateSuccess: PropTypes.func,
  /**
   * Initial fields to pre populate the form
   */
  initialFields: PropTypes.shape({
    title: PropTypes.string,
    assignedTo: PropTypes.oneOf([
      PropTypes.arrayOf(
        PropTypes.shape({ id: PropTypes.string.isRequired, type: PropTypes.string }),
      ),
      PropTypes.string,
    ]),
    dueDate: PropTypes.instanceOf(Date),
    question: PropTypes.string,
    suggestedAnswer: PropTypes.string,
    locationDescription: PropTypes.string,
    priority: PropTypes.oneOf(Object.values(RfiPriority)),
  }),
};

RfiCreateForm.defaultProps = {
  open: false,
  onCreateSuccess: () => {},
  onClose: () => {},
  initialFields: {},
};

export default RfiCreateForm;
